import { CmsMeta } from '../cmsMeta';
import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from '../../../i18n/i18n';
import { UiContainer } from '../../../state/ui';
import './CmsFaq.css';

type CmsFaqProps = {
	children: ReactNode;
	defaultAudience?: 'company' | 'private';
	hideSwitchBtns?: boolean;
};

export const CmsFaq = ({ children, defaultAudience = 'company', hideSwitchBtns }: CmsFaqProps): JSX.Element => {
	const [audience, setAudience] = useState<'company' | 'private'>(defaultAudience);
	const t = useTranslation().customerTypeModal;
	const ui = UiContainer.useContainer();
	const selectAudience = (value: 'company' | 'private') => ui.setPriceWithVat(value === 'private');

	useEffect(() => {
		if (ui.priceWithVat !== null) {
			const uiAudience = ui.priceWithVat ? 'private' : 'company';
			setAudience(uiAudience);
		}
	}, [ui]);

	return (
		<div className={`cms-faq cms-faq--${audience}`} data-testid="cms-faq">
			{!hideSwitchBtns && (
				<div className="cms-faq__switch" role="group" aria-label={t.selectAudience}>
					<button
						type="button"
						name="company"
						className={`cms-faq__switch-button ${audience === 'company' ? 'is-active' : ''}`}
						onClick={() => selectAudience('company')}
						aria-pressed={audience === 'company'}
					>
						{t.company}
					</button>
					<button
						type="button"
						name="private"
						className={`cms-faq__switch-button ${audience === 'private' ? 'is-active' : ''}`}
						onClick={() => selectAudience('private')}
						aria-pressed={audience === 'private'}
					>
						{t.private}
					</button>
				</div>
			)}
			<div className="cms-faq__questions">{children}</div>
		</div>
	);
};

export const cmsMeta: CmsMeta = {
	description:
		"The frame around a whole FAQ: headings and questions go inside it. `defaultAudience` is only the value the first render starts from - as soon as the reader's vat setting is known it decides, showing the private answer to anyone seeing prices including vat. Treat it as a fallback, not as a way to choose what a reader sees.",
	example: [
		'CmsFaq',
		{ index: 1, defaultAudience: 'company' },
		['CmsFaqHeading', { index: 0, title: 'Bestellen & Kunde werden' }],
		[
			'CmsFaqQuestion',
			{
				index: 1,
				question: 'Brauche ich ein Geschäftskonto?',
				answer: 'Nein, Sie können auch ohne Konto bestellen.',
			},
		],
	],
};
